// store/posStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartItem {
    id: string;
    productId: string;
    productName: string;
    variantId?: string;
    variantName?: string;
    uomId: string;
    uomName: string;
    uomAbbreviation: string;
    quantity: number;
    unitPrice: number;
    discountAmount: number;
    taxAmount: number;
    totalAmount: number;
    imageUrl?: string;
    stockCondition?: 'new' | 'damaged' | 'open_box' | 'expiring' | 'refurbished';
    conditionPrice?: number;
    notes?: string;
}

export interface PaymentItem {
    paymentMethod: 'cash' | 'bank_transfer' | 'pos_card' | 'card' | 'mobile_money' | 'cheque' | 'other';
    amount: number;
    reference?: string;
    bankAccountId?: string;
    mobileMoneyProvider?: string;
}

export interface CustomerInfo {
    id: string;
    name: string;
    phone: string;
    loyaltyCardNumber: string;
    loyaltyPoints: number;
    loyaltyTier: 'bronze' | 'silver' | 'gold' | 'platinum';
    storeCredit: number;
    tierDiscountPercent: number;
    dateOfBirth?: string;
}

export type CartContext = 'retail' | 'restaurant';

export interface CartTab {
    id: string;
    name: string;
    cart: CartItem[];
    subtotal: number;
    discountTotal: number;
    taxTotal: number;
    grandTotal: number;
    customerId: string | null;
    customerName: string | null;
    customerInfo: CustomerInfo | null;
    customerVerification: 'unverified' | 'pending' | 'verified' | null;
    saleType: 'retail' | 'wholesale' | 'clearance';
    tableId: string | null;
    tableName: string | null;
    createdAt: number;
}

interface PosState {
    // Multi-cart Tabs
    tabs: CartTab[];
    activeTabId: string;
    switchTab: (tabId: string) => void;
    addTab: (customName?: string) => string;
    closeTab: (tabId: string) => void;
    renameTab: (tabId: string, name: string) => void;

    // Cart context (retail vs restaurant)
    cartContext: CartContext;

    // Active Cart Details
    cart: CartItem[];
    subtotal: number;
    discountTotal: number;
    taxTotal: number;
    grandTotal: number;

    // Customer
    customerId: string | null;
    customerName: string | null;
    customerInfo: CustomerInfo | null;
    customerVerification: 'unverified' | 'pending' | 'verified' | null;

    // Sale Type
    saleType: 'retail' | 'wholesale' | 'clearance';

    // Table (for restaurants)
    tableId: string | null;
    tableName: string | null;

    // Shift
    activeShiftId: string | null;
    branchId: string;

    // UI State
    isCartDrawerOpen: boolean;
    isPaymentModalOpen: boolean;
    lastScannedBarcode: string | null;

    // Actions
    setCartContext: (context: CartContext) => void;
    addItem: (item: CartItem) => void;
    removeItem: (id: string) => void;
    updateQuantity: (id: string, quantity: number) => void;
    updateDiscount: (id: string, amount: number) => void;
    updateStockCondition: (id: string, condition: CartItem['stockCondition']) => void;
    clearCart: () => void;
    recalculateTotals: () => void;

    setCustomer: (id: string | null, name: string | null, info?: CustomerInfo | null) => void;
    setCustomerVerification: (status: 'unverified' | 'pending' | 'verified' | null) => void;
    setSaleType: (type: 'retail' | 'wholesale' | 'clearance') => void;
    setTable: (id: string | null, name: string | null) => void;
    setShift: (id: string | null, branchId: string) => void;

    setCartDrawerOpen: (open: boolean) => void;
    setPaymentModalOpen: (open: boolean) => void;
    setLastScannedBarcode: (barcode: string | null) => void;
}

const TAX_RATE = 0;

const createDefaultTab = (id = 'tab-1', name = 'Cart 1'): CartTab => ({
    id,
    name,
    cart: [],
    subtotal: 0,
    discountTotal: 0,
    taxTotal: 0,
    grandTotal: 0,
    customerId: null,
    customerName: null,
    customerInfo: null,
    customerVerification: null,
    saleType: 'retail',
    tableId: null,
    tableName: null,
    createdAt: Date.now(),
});

export const usePosStore = create<PosState>()(
    persist(
        (set, get) => ({
            // Multi-cart state
            tabs: [createDefaultTab()],
            activeTabId: 'tab-1',

            // Cart context - default to retail
            cartContext: 'retail',

            // Initial active cart state
            cart: [],
            subtotal: 0,
            discountTotal: 0,
            taxTotal: 0,
            grandTotal: 0,
            customerId: null,
            customerName: null,
            customerInfo: null,
            customerVerification: null,
            saleType: 'retail',
            tableId: null,
            tableName: null,
            activeShiftId: null,
            branchId: '00000000-0000-0000-0000-000000000000',
            isCartDrawerOpen: false,
            isPaymentModalOpen: false,
            lastScannedBarcode: null,

            // Switch to another cart tab
            switchTab: (tabId: string) => {
                const currentTabs = get().tabs && get().tabs.length > 0 ? get().tabs : [createDefaultTab(get().activeTabId || 'tab-1')];
                const targetTab = currentTabs.find(t => t.id === tabId);
                if (!targetTab) return;

                // Sync currently active state into its tab
                const currentActiveId = get().activeTabId;
                const syncedTabs = currentTabs.map(t => {
                    if (t.id === currentActiveId) {
                        return {
                            ...t,
                            cart: get().cart,
                            subtotal: get().subtotal,
                            discountTotal: get().discountTotal,
                            taxTotal: get().taxTotal,
                            grandTotal: get().grandTotal,
                            customerId: get().customerId,
                            customerName: get().customerName,
                            customerInfo: get().customerInfo,
                            customerVerification: get().customerVerification,
                            saleType: get().saleType,
                            tableId: get().tableId,
                            tableName: get().tableName,
                        };
                    }
                    return t;
                });

                set({
                    tabs: syncedTabs,
                    activeTabId: targetTab.id,
                    cart: targetTab.cart || [],
                    subtotal: targetTab.subtotal || 0,
                    discountTotal: targetTab.discountTotal || 0,
                    taxTotal: targetTab.taxTotal || 0,
                    grandTotal: targetTab.grandTotal || 0,
                    customerId: targetTab.customerId,
                    customerName: targetTab.customerName,
                    customerInfo: targetTab.customerInfo,
                    customerVerification: targetTab.customerVerification,
                    saleType: targetTab.saleType || 'retail',
                    tableId: targetTab.tableId,
                    tableName: targetTab.tableName,
                });
            },

            // Add a new cart tab
            addTab: (customName?: string) => {
                const currentTabs = get().tabs && get().tabs.length > 0 ? get().tabs : [createDefaultTab(get().activeTabId || 'tab-1')];
                const currentActiveId = get().activeTabId;

                // Sync current active cart before adding
                const syncedTabs = currentTabs.map(t => {
                    if (t.id === currentActiveId) {
                        return {
                            ...t,
                            cart: get().cart,
                            subtotal: get().subtotal,
                            discountTotal: get().discountTotal,
                            taxTotal: get().taxTotal,
                            grandTotal: get().grandTotal,
                            customerId: get().customerId,
                            customerName: get().customerName,
                            customerInfo: get().customerInfo,
                            customerVerification: get().customerVerification,
                            saleType: get().saleType,
                            tableId: get().tableId,
                            tableName: get().tableName,
                        };
                    }
                    return t;
                });

                const newId = `tab-${Date.now()}`;
                const newName = customName || `Cart ${syncedTabs.length + 1}`;
                const newTab = createDefaultTab(newId, newName);

                set({
                    tabs: [...syncedTabs, newTab],
                    activeTabId: newId,
                    cart: [],
                    subtotal: 0,
                    discountTotal: 0,
                    taxTotal: 0,
                    grandTotal: 0,
                    customerId: null,
                    customerName: null,
                    customerInfo: null,
                    customerVerification: null,
                    saleType: 'retail',
                    tableId: null,
                    tableName: null,
                });

                return newId;
            },

            // Close a cart tab
            closeTab: (tabId: string) => {
                const currentTabs = get().tabs && get().tabs.length > 0 ? get().tabs : [createDefaultTab()];
                if (currentTabs.length <= 1) {
                    get().clearCart();
                    return;
                }

                const remainingTabs = currentTabs.filter(t => t.id !== tabId);
                if (get().activeTabId === tabId) {
                    const nextTab = remainingTabs[remainingTabs.length - 1];
                    set({
                        tabs: remainingTabs,
                        activeTabId: nextTab.id,
                        cart: nextTab.cart || [],
                        subtotal: nextTab.subtotal || 0,
                        discountTotal: nextTab.discountTotal || 0,
                        taxTotal: nextTab.taxTotal || 0,
                        grandTotal: nextTab.grandTotal || 0,
                        customerId: nextTab.customerId,
                        customerName: nextTab.customerName,
                        customerInfo: nextTab.customerInfo,
                        customerVerification: nextTab.customerVerification,
                        saleType: nextTab.saleType || 'retail',
                        tableId: nextTab.tableId,
                        tableName: nextTab.tableName,
                    });
                } else {
                    set({ tabs: remainingTabs });
                }
            },

            // Rename a cart tab
            renameTab: (tabId: string, name: string) => {
                const currentTabs = get().tabs || [];
                const updated = currentTabs.map(t => t.id === tabId ? { ...t, name } : t);
                set({ tabs: updated });
            },

            // Set cart context - clears cart when switching
            setCartContext: (context: CartContext) => {
                const current = get();
                if (current.cartContext !== context) {
                    set({
                        cartContext: context,
                        cart: [],
                        subtotal: 0,
                        discountTotal: 0,
                        taxTotal: 0,
                        grandTotal: 0,
                        customerId: null,
                        customerName: null,
                        customerInfo: null,
                        customerVerification: null,
                        tableId: null,
                        tableName: null,
                        isCartDrawerOpen: false,
                        tabs: [createDefaultTab()],
                        activeTabId: 'tab-1',
                    });
                }
            },

            // Add item to cart
            addItem: (item: CartItem) => {
                const state = get();
                const existingIndex = state.cart.findIndex(
                    (i) => i.productId === item.productId && i.stockCondition === item.stockCondition
                );

                let newCart: CartItem[];

                if (existingIndex >= 0) {
                    newCart = [...state.cart];
                    newCart[existingIndex] = {
                        ...newCart[existingIndex],
                        quantity: newCart[existingIndex].quantity + 1,
                        totalAmount: (newCart[existingIndex].quantity + 1) * newCart[existingIndex].unitPrice - (newCart[existingIndex].discountAmount || 0),
                        taxAmount: ((newCart[existingIndex].quantity + 1) * newCart[existingIndex].unitPrice - (newCart[existingIndex].discountAmount || 0)) * TAX_RATE,
                    };
                } else {
                    const newItem: CartItem = {
                        ...item,
                        id: `cart-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                        quantity: 1,
                        totalAmount: item.unitPrice - (item.discountAmount || 0),
                        taxAmount: (item.unitPrice - (item.discountAmount || 0)) * TAX_RATE,
                    };
                    newCart = [...state.cart, newItem];
                }

                set({ cart: newCart });
                get().recalculateTotals();
            },

            // Remove item from cart
            removeItem: (id: string) => {
                const newCart = get().cart.filter((i) => i.id !== id);
                set({ cart: newCart });
                get().recalculateTotals();
            },

            // Update quantity
            updateQuantity: (id: string, quantity: number) => {
                if (quantity <= 0) {
                    get().removeItem(id);
                    return;
                }

                const newCart = get().cart.map((item) => {
                    if (item.id !== id) return item;
                    const lineTotal = quantity * item.unitPrice - (item.discountAmount || 0);
                    return {
                        ...item,
                        quantity,
                        totalAmount: lineTotal,
                        taxAmount: lineTotal * TAX_RATE,
                    };
                });

                set({ cart: newCart });
                get().recalculateTotals();
            },

            // Update discount on an item
            updateDiscount: (id: string, amount: number) => {
                const newCart = get().cart.map((item) => {
                    if (item.id !== id) return item;
                    const lineTotal = item.quantity * item.unitPrice - amount;
                    return {
                        ...item,
                        discountAmount: amount,
                        totalAmount: Math.max(0, lineTotal),
                        taxAmount: Math.max(0, lineTotal) * TAX_RATE,
                    };
                });

                set({ cart: newCart });
                get().recalculateTotals();
            },

            // Update stock condition
            updateStockCondition: (id: string, condition: CartItem['stockCondition']) => {
                const newCart = get().cart.map((item) => {
                    if (item.id !== id) return item;
                    let price = item.unitPrice;
                    switch (condition) {
                        case 'damaged': price = item.unitPrice * 0.5; break;
                        case 'open_box': price = item.unitPrice * 0.8; break;
                        case 'expiring': price = item.unitPrice * 0.6; break;
                        case 'refurbished': price = item.unitPrice * 0.7; break;
                        default: price = item.unitPrice;
                    }
                    const lineTotal = item.quantity * price - (item.discountAmount || 0);
                    return {
                        ...item,
                        stockCondition: condition,
                        conditionPrice: price,
                        unitPrice: price,
                        totalAmount: Math.max(0, lineTotal),
                        taxAmount: Math.max(0, lineTotal) * TAX_RATE,
                    };
                });

                set({ cart: newCart });
                get().recalculateTotals();
            },

            // Clear cart
            clearCart: () => {
                const currentActiveId = get().activeTabId || 'tab-1';
                const currentTabs = get().tabs && get().tabs.length > 0 ? get().tabs : [createDefaultTab(currentActiveId)];
                const updatedTabs = currentTabs.map(t => {
                    if (t.id === currentActiveId) {
                        return {
                            ...t,
                            cart: [],
                            subtotal: 0,
                            discountTotal: 0,
                            taxTotal: 0,
                            grandTotal: 0,
                            customerId: null,
                            customerName: null,
                            customerInfo: null,
                            customerVerification: null,
                            tableId: null,
                            tableName: null,
                        };
                    }
                    return t;
                });

                set({
                    cart: [],
                    subtotal: 0,
                    discountTotal: 0,
                    taxTotal: 0,
                    grandTotal: 0,
                    customerId: null,
                    customerName: null,
                    customerInfo: null,
                    customerVerification: null,
                    tableId: null,
                    tableName: null,
                    isCartDrawerOpen: false,
                    tabs: updatedTabs,
                });
            },

            // Recalculate all totals
            recalculateTotals: () => {
                const { cart, customerInfo, activeTabId, tabs } = get();

                const subtotal = cart.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
                const discountTotal = cart.reduce((sum, item) => sum + (item.discountAmount || 0), 0);

                const tierDiscount = customerInfo?.tierDiscountPercent
                    ? (subtotal - discountTotal) * (customerInfo.tierDiscountPercent / 100)
                    : 0;

                const afterDiscounts = subtotal - discountTotal - tierDiscount;
                const taxTotal = afterDiscounts * TAX_RATE;
                const grandTotal = afterDiscounts + taxTotal;

                const roundedSubtotal = Math.round(subtotal * 100) / 100;
                const roundedDiscountTotal = Math.round(discountTotal * 100) / 100;
                const roundedTaxTotal = Math.round(taxTotal * 100) / 100;
                const roundedGrandTotal = Math.round(grandTotal * 100) / 100;

                const currentActiveId = activeTabId || 'tab-1';
                const currentTabs = tabs && tabs.length > 0 ? tabs : [createDefaultTab(currentActiveId)];
                const updatedTabs = currentTabs.map(t => {
                    if (t.id === currentActiveId) {
                        return {
                            ...t,
                            cart,
                            subtotal: roundedSubtotal,
                            discountTotal: roundedDiscountTotal,
                            taxTotal: roundedTaxTotal,
                            grandTotal: roundedGrandTotal,
                            customerId: get().customerId,
                            customerName: get().customerName,
                            customerInfo: get().customerInfo,
                            customerVerification: get().customerVerification,
                            saleType: get().saleType,
                            tableId: get().tableId,
                            tableName: get().tableName,
                        };
                    }
                    return t;
                });

                set({
                    subtotal: roundedSubtotal,
                    discountTotal: roundedDiscountTotal,
                    taxTotal: roundedTaxTotal,
                    grandTotal: roundedGrandTotal,
                    tabs: updatedTabs,
                });
            },

            // Customer management
            setCustomer: (id: string | null, name: string | null, info?: CustomerInfo | null) => {
                set({
                    customerId: id,
                    customerName: name,
                    customerInfo: info || null,
                    customerVerification: id ? 'pending' : null,
                });
                if (id) get().recalculateTotals();
            },

            setCustomerVerification: (status) => {
                set({ customerVerification: status });
            },

            setSaleType: (type) => {
                set({ saleType: type });
                get().recalculateTotals();
            },

            setTable: (id, name) => {
                set({ tableId: id, tableName: name });
            },

            setShift: (id, branchId) => {
                if (!id) {
                    set({
                        activeShiftId: null,
                        branchId: branchId || '',
                        cart: [],
                        subtotal: 0,
                        discountTotal: 0,
                        taxTotal: 0,
                        grandTotal: 0,
                        customerId: null,
                        customerName: null,
                        customerInfo: null,
                        customerVerification: null,
                        tableId: null,
                        tableName: null,
                        tabs: [createDefaultTab()],
                        activeTabId: 'tab-1',
                    });
                } else {
                    set({ activeShiftId: id, branchId });
                }
            },

            // UI State
            setCartDrawerOpen: (open) => set({ isCartDrawerOpen: open }),
            setPaymentModalOpen: (open) => set({ isPaymentModalOpen: open }),
            setLastScannedBarcode: (barcode) => set({ lastScannedBarcode: barcode }),
        }),
        {
            name: 'pos-store',
            partialize: (state) => ({
                cartContext: state.cartContext,
                cart: state.cart,
                subtotal: state.subtotal,
                discountTotal: state.discountTotal,
                taxTotal: state.taxTotal,
                grandTotal: state.grandTotal,
                customerId: state.customerId,
                customerName: state.customerName,
                customerInfo: state.customerInfo,
                saleType: state.saleType,
                tableId: state.tableId,
                tableName: state.tableName,
                branchId: state.branchId,
                tabs: state.tabs,
                activeTabId: state.activeTabId,
            }),
        }
    )
);